import React, { useState, useMemo } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import { FILTER_OPTIONS } from "../data/catalog";

const Section = ({ title, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-leduro-border last:border-b-0" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-4 px-5 text-left"
      >
        <span className="leduro-eyebrow !text-leduro-navy">{title}</span>
        <ChevronDown className={`h-4 w-4 text-leduro-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
};

const Pill = ({ active, onClick, children, testId, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
      disabled
        ? "border-leduro-border/60 text-leduro-muted/50 cursor-not-allowed"
        : active
        ? "border-leduro-orange bg-leduro-orange text-white"
        : "border-leduro-border text-leduro-navy hover:border-leduro-orange hover:text-leduro-orange"
    }`}
  >
    {children}
  </button>
);

const Range = ({ min, max, valueMin, valueMax, onMin, onMax, unit }) => (
  <div>
    <div className="flex items-center justify-between text-xs font-mono text-leduro-navy mb-3">
      <span>{valueMin}{unit ? ` ${unit}` : ""}</span>
      <span>{valueMax.toLocaleString()}{unit ? ` ${unit}` : ""}</span>
    </div>
    <div className="relative h-1 bg-leduro-surface-2">
      <div
        className="absolute h-1 bg-leduro-orange"
        style={{
          left: `${((valueMin - min) / (max - min)) * 100}%`,
          right: `${100 - ((valueMax - min) / (max - min)) * 100}%`,
        }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={valueMin}
        onChange={(e) => onMin(Math.min(parseInt(e.target.value), valueMax))}
        className="absolute inset-0 w-full h-1 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:bg-leduro-orange [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:bg-leduro-orange [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer"
      />
      <input
        type="range"
        min={min}
        max={max}
        value={valueMax}
        onChange={(e) => onMax(Math.max(parseInt(e.target.value), valueMin))}
        className="absolute inset-0 w-full h-1 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:bg-leduro-orange [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:bg-leduro-orange [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
    <div className="flex items-center justify-between text-[10px] font-mono text-leduro-muted mt-3">
      <span>{min}{unit ? ` ${unit}` : ""}</span>
      <span>{max.toLocaleString()}{unit ? ` ${unit}` : ""}</span>
    </div>
  </div>
);

export const CatalogueFilters = ({
  filters,
  setFilters,
  query,
  setQuery,
  counts,
  onReset,
  totalAll,
}) => {
  const update = (patch) => setFilters({ ...filters, ...patch });
  const toggle = (key, val) => {
    const set = new Set(filters[key]);
    set.has(val) ? set.delete(val) : set.add(val);
    update({ [key]: [...set] });
  };

  return (
    <div className="border border-leduro-border bg-white" data-testid="catalogue-filters">
      <div className="flex items-center justify-between px-5 py-4 border-b border-leduro-border">
        <span className="leduro-eyebrow !text-leduro-navy">— Filters</span>
        <button
          onClick={onReset}
          data-testid="filters-reset"
          className="text-[11px] uppercase tracking-wider font-bold text-leduro-orange hover:underline inline-flex items-center gap-1"
        >
          <X className="h-3 w-3" /> Reset
        </button>
      </div>

      <div className="p-5 border-b border-leduro-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-leduro-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by SKU, name…"
            data-testid="catalogue-search-input"
            className="w-full pl-10 pr-3 py-2.5 border border-leduro-border focus:outline-none focus:border-leduro-orange text-sm"
          />
        </div>
      </div>

      <Section title="Lumens" testId="filter-lumens">
        <Range
          min={FILTER_OPTIONS.lumenMin}
          max={FILTER_OPTIONS.lumenMax}
          valueMin={filters.lumenMin}
          valueMax={filters.lumenMax}
          onMin={(v) => update({ lumenMin: v })}
          onMax={(v) => update({ lumenMax: v })}
          unit="lm"
        />
      </Section>

      <Section title="Wattage" testId="filter-wattage">
        <Range
          min={FILTER_OPTIONS.wattMin}
          max={FILTER_OPTIONS.wattMax}
          valueMin={filters.wattMin}
          valueMax={filters.wattMax}
          onMin={(v) => update({ wattMin: v })}
          onMax={(v) => update({ wattMax: v })}
          unit="W"
        />
      </Section>

      <Section title="Colour Temperature" testId="filter-cct">
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.cct.map((k) => (
            <Pill
              key={k}
              active={filters.cct.includes(k)}
              onClick={() => toggle("cct", k)}
              testId={`cct-${k}`}
            >
              {k}K
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Socket / Connector" testId="filter-socket" defaultOpen={false}>
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.sockets.map((s) => (
            <Pill
              key={s}
              active={filters.sockets.includes(s)}
              onClick={() => toggle("sockets", s)}
              testId={`socket-${s}`}
            >
              {s}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="IP Rating" testId="filter-ip">
        <ul className="space-y-2">
          {FILTER_OPTIONS.ips.map((ip) => {
            const checked = filters.ips.includes(ip.code);
            const cnt = counts.ips?.[ip.code] || 0;
            return (
              <li key={ip.code}>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="flex items-center gap-2.5 text-sm text-leduro-navy">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle("ips", ip.code)}
                      data-testid={`ip-${ip.code}`}
                      className="accent-leduro-orange h-4 w-4"
                    />
                    {ip.label}
                  </span>
                  <span className="text-xs font-mono text-leduro-muted">{cnt}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="Warranty" testId="filter-warranty">
        <ul className="space-y-2">
          {FILTER_OPTIONS.warranties.map((y) => {
            const checked = filters.warranties.includes(y);
            const cnt = counts.warranties?.[y] || 0;
            return (
              <li key={y}>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="flex items-center gap-2.5 text-sm text-leduro-navy">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle("warranties", y)}
                      data-testid={`warranty-${y}`}
                      className="accent-leduro-orange h-4 w-4"
                    />
                    {y} years
                  </span>
                  <span className="text-xs font-mono text-leduro-muted">{cnt}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="CRI" testId="filter-cri" defaultOpen={false}>
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.cri.map((c) => (
            <Pill
              key={c}
              active={filters.cri.includes(c)}
              onClick={() => toggle("cri", c)}
              testId={`cri-${c}`}
            >
              Ra {c}+
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Features" testId="filter-features" defaultOpen={false}>
        <ul className="space-y-2">
          {FILTER_OPTIONS.features.map((f) => {
            const checked = filters.features.includes(f.key);
            return (
              <li key={f.key}>
                <label className="flex items-center gap-2.5 text-sm text-leduro-navy cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle("features", f.key)}
                    data-testid={`feature-${f.key}`}
                    className="accent-leduro-orange h-4 w-4"
                  />
                  {f.label}
                </label>
              </li>
            );
          })}
        </ul>
      </Section>

      <div className="bg-leduro-navy text-white p-5">
        <p className="leduro-eyebrow text-leduro-orange">Trade buyer?</p>
        <p className="text-sm mt-2 leading-relaxed text-white/80">
          Volume pricing, dedicated engineer and bespoke datasheets for orders 100+ units.
        </p>
        <a href="/login" data-testid="sidebar-trade-cta" className="leduro-btn-primary !py-2 !px-3 text-[11px] mt-4">
          Open Trade Account
        </a>
      </div>
    </div>
  );
};

export default CatalogueFilters;
